import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import {
  Building2, Palette, Store, Users, Wallet, RefreshCw, Save, ShieldCheck,
  ChevronRight, Globe, Mail, Image as ImageIcon, Tag, Percent, Plus,
  Link2, Unlink, CheckCircle2, AlertTriangle,
} from 'lucide-react'
import { getSession } from '../lib/session'

// White-label / Reseller console.
//   • Branding card (every workspace): edit tenant_branding — brand name, logo,
//     primary color, support email, custom domain — with a live preview swatch.
//     Saves to PUT /api/v1/reseller/branding.
//   • Operator plane (platform admins only): the resellers registry, each
//     reseller's managed workspaces, and its commission ledger. If the
//     operator endpoints 403 for a non-admin, that whole plane hides itself and
//     only Branding shows.
//
// VISUAL LAYER: the flat "Nunito / Intelligence Briefings" aesthetic — soft
// palette, 20px rounded cards, pill tags, light+dark theme tokens — scoped
// under `.rs-root` so nothing leaks. Mirrors pages/GrowthLab.jsx verbatim.
const API = '/api/v1/reseller'

// ---- scoped stylesheet (prefixed .rs-* so nothing leaks) -------------------
const RS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
.rs-root{
  --rs-bg:#FBFBF7; --rs-panel:#FFFFFF; --rs-panel2:#F6F6F0; --rs-line:#ECEBE3;
  --rs-ink:#2E3A45; --rs-mut:#7C8794; --rs-dim:#AEB6BD;
  --rs-accent:#4E9AC4; --rs-up:#4FAE5A; --rs-down:#E24A3B; --rs-warn:#EAB43C; --rs-purple:#8E7CC3;
  --rs-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  --rs-shadow-lg:0 10px 24px rgba(46,58,69,.10),0 34px 66px rgba(46,58,69,.15);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--rs-ink); -webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .rs-root{
  --rs-bg:#0E1116; --rs-panel:#171B22; --rs-panel2:#12161C; --rs-line:#262C36;
  --rs-ink:#E7ECF1; --rs-mut:#95A0AD; --rs-dim:#5C6672;
  --rs-accent:#5FB0DB; --rs-up:#5CC47C; --rs-down:#F0575C; --rs-warn:#EAB43C; --rs-purple:#A796D6;
  --rs-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --rs-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 34px 70px rgba(0,0,0,.6);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .rs-root{
  --rs-bg:#0E1116; --rs-panel:#171B22; --rs-panel2:#12161C; --rs-line:#262C36;
  --rs-ink:#E7ECF1; --rs-mut:#95A0AD; --rs-dim:#5C6672;
  --rs-accent:#5FB0DB; --rs-up:#5CC47C; --rs-down:#F0575C; --rs-warn:#EAB43C; --rs-purple:#A796D6;
  --rs-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --rs-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 34px 70px rgba(0,0,0,.6);
}}
.rs-root *{box-sizing:border-box;}
.rs-title{font-size:24px;font-weight:800;letter-spacing:-.02em;margin:0;display:flex;align-items:center;gap:10px;color:var(--rs-ink);}
.rs-sub{margin:4px 0 0;color:var(--rs-mut);font-size:14px;font-weight:500;line-height:1.5;}
.rs-card{background:var(--rs-panel);border:1px solid var(--rs-line);border-radius:20px;box-shadow:var(--rs-shadow);padding:20px 22px;}
.rs-card.tight{padding:16px 18px;}
.rs-soft{background:var(--rs-panel2);border:1px solid var(--rs-line);border-radius:14px;}
.rs-sec-h{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:var(--rs-mut);display:flex;align-items:center;gap:8px;margin:0;}
.rs-elab{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--rs-mut);margin:0;display:flex;align-items:center;gap:6px;}
.rs-narr{font-size:15px;font-weight:700;line-height:1.5;color:var(--rs-ink);margin:0;}
.rs-body{font-size:14px;font-weight:600;line-height:1.55;color:var(--rs-ink);margin:0;}
.rs-muted{color:var(--rs-mut);font-weight:600;}
.rs-dim{color:var(--rs-dim);font-weight:600;}
.rs-tabnum{font-variant-numeric:tabular-nums;}
.rs-lbl{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:var(--rs-mut);display:flex;align-items:center;gap:6px;margin:0 0 6px;}
.rs-input{width:100%;font-family:Nunito,system-ui,sans-serif;font-size:14px;font-weight:600;color:var(--rs-ink);background:var(--rs-panel2);border:1px solid var(--rs-line);border-radius:12px;padding:10px 13px;transition:.16s;}
.rs-input:focus{outline:none;border-color:var(--rs-accent);box-shadow:0 0 0 3px rgba(78,154,196,.15);}
.rs-input::placeholder{color:var(--rs-dim);font-weight:600;}
.rs-pill{font-size:11px;font-weight:800;padding:4px 11px;border-radius:20px;display:inline-flex;align-items:center;gap:5px;text-transform:capitalize;}
.rs-chip{font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;background:var(--rs-panel2);border:1px solid var(--rs-line);color:var(--rs-mut);display:inline-flex;align-items:center;gap:5px;white-space:nowrap;}
.rs-mchip{font-size:11px;font-weight:800;padding:5px 11px;border-radius:9px;background:var(--rs-panel2);border:1px solid var(--rs-line);color:var(--rs-mut);display:inline-flex;align-items:center;gap:6px;white-space:nowrap;}
.rs-icobox{flex:none;width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;}
.rs-btn{font-family:Nunito,system-ui,sans-serif;font-size:13px;font-weight:800;border-radius:12px;padding:9px 15px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;border:1px solid var(--rs-line);background:var(--rs-panel);color:var(--rs-ink);transition:.16s;box-shadow:var(--rs-shadow);}
.rs-btn:hover{transform:translateY(-1px);}
.rs-btn:disabled{opacity:.6;cursor:default;transform:none;}
.rs-btn.sm{font-size:12px;padding:7px 13px;border-radius:10px;}
.rs-btn.primary{background:var(--rs-accent);color:#fff;border-color:transparent;}
.rs-btn.ghost{background:none;border:none;box-shadow:none;color:var(--rs-mut);padding:7px 9px;font-weight:700;}
.rs-btn.ghost:hover{color:var(--rs-ink);}
.rs-btn.link{background:none;border:none;box-shadow:none;padding:0;color:var(--rs-accent);font-size:12.5px;font-weight:800;}
.rs-btn.link:hover{transform:none;filter:brightness(1.08);}
.rs-btn.danger{background:none;border:none;box-shadow:none;color:var(--rs-down);padding:7px 10px;}
.rs-btn.danger:hover{transform:none;filter:brightness(1.05);}
.rs-row-toggle{width:100%;text-align:left;cursor:pointer;transition:.16s;}
.rs-row-toggle:hover{border-color:var(--rs-accent);transform:translateY(-1px);}
.rs-spin{animation:rs-spin 1s linear infinite;}
@keyframes rs-spin{to{transform:rotate(360deg);}}
.rs-fade{animation:rs-fade .35s ease both;}
@keyframes rs-fade{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
.rs-swatch{width:44px;height:44px;border-radius:12px;border:1px solid var(--rs-line);flex:none;box-shadow:inset 0 0 0 3px var(--rs-panel);}
.rs-logo{width:44px;height:44px;border-radius:12px;border:1px solid var(--rs-line);object-fit:contain;background:var(--rs-panel2);flex:none;}
`

let _rsStyleInjected = false
function RSStyle() {
  useEffect(() => {
    if (_rsStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-rs-style', '1')
    el.textContent = RS_CSS
    document.head.appendChild(el)
    _rsStyleInjected = true
  }, [])
  return null
}

// ---- helpers --------------------------------------------------------------
const TONE = {
  up:   { c: 'var(--rs-up)',     bg: 'rgba(79,174,90,.15)' },
  down: { c: 'var(--rs-down)',   bg: 'rgba(226,74,59,.14)' },
  warn: { c: 'var(--rs-warn)',   bg: 'rgba(234,180,60,.16)' },
  acc:  { c: 'var(--rs-accent)', bg: 'rgba(78,154,196,.14)' },
  pur:  { c: 'var(--rs-purple)', bg: 'rgba(142,124,195,.16)' },
  mut:  { c: 'var(--rs-mut)',    bg: 'rgba(124,135,148,.14)' },
}
const pill = (t) => ({ background: t.bg, color: t.c })

const titleCase = (s) => String(s || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
const asText = (v) => (v == null ? '' : typeof v === 'object' ? '' : String(v))
const usd = (cents) => {
  const n = Number(cents)
  if (!Number.isFinite(n)) return '$0.00'
  return (n / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}
const fmtDate = (v) => {
  if (!v) return ''
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
const isHex = (s) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(s || '').trim())

const statusTone = (s) => {
  const v = String(s || '').toLowerCase()
  if (v === 'active') return TONE.up
  if (v === 'suspended' || v === 'canceled' || v === 'cancelled') return TONE.down
  if (v === 'trial' || v === 'pending') return TONE.warn
  return TONE.mut
}

export default function Reseller() {
  const session = getSession()

  return (
    <div className="rs-root space-y-8">
      <RSStyle />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="rs-title">
            <Building2 size={24} style={{ color: 'var(--rs-accent)' }} /> White-label
          </h2>
          <p className="rs-sub">
            Brand this workspace as your own — logo, name, and color. Platform operators also manage resellers, their workspaces, and commissions.
          </p>
        </div>
        <span className="rs-pill" style={pill(TONE.acc)}>
          <Store size={13} /> Partner &amp; branding
        </span>
      </div>

      {!session?.namespaceId && (
        <div className="rs-card" style={{ textAlign: 'center', padding: '48px 22px' }}>
          <Building2 size={32} style={{ color: 'var(--rs-dim)', margin: '0 auto 12px' }} />
          <p className="rs-muted" style={{ margin: 0 }}>Connect a workspace to manage white-label branding.</p>
        </div>
      )}

      {session?.namespaceId && (
        <>
          <BrandingCard />
          <OperatorPlane />
        </>
      )}
    </div>
  )
}

// ---- BRANDING (tenant-facing) ---------------------------------------------
const EMPTY_BRAND = { brand_name: '', logo_url: '', primary_color: '', support_email: '', custom_domain: '' }

function BrandingCard() {
  const [form, setForm] = useState(EMPTY_BRAND)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [updatedAt, setUpdatedAt] = useState(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    axios.get(`${API}/branding`)
      .then(r => {
        if (!alive) return
        const b = r.data?.branding || {}
        setForm({
          brand_name: asText(b.brand_name),
          logo_url: asText(b.logo_url),
          primary_color: asText(b.primary_color),
          support_email: asText(b.support_email),
          custom_domain: asText(b.custom_domain),
        })
        setUpdatedAt(b.updated_at || null)
      })
      .catch(() => { if (alive) setForm(EMPTY_BRAND) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))

  const save = async () => {
    if (form.primary_color && !isHex(form.primary_color)) {
      toast.error('Primary color must be a hex value like #4E9AC4')
      return
    }
    setSaving(true)
    try {
      // Send empty strings as null so the backend COALESCE keeps prior values untouched
      // only when a field is intentionally cleared vs. simply not edited. We send the
      // current field values; empty → null clears them.
      const payload = {
        brand_name: form.brand_name.trim() || null,
        logo_url: form.logo_url.trim() || null,
        primary_color: form.primary_color.trim() || null,
        support_email: form.support_email.trim() || null,
        custom_domain: form.custom_domain.trim() || null,
      }
      const r = await axios.put(`${API}/branding`, payload)
      const b = r.data?.branding || {}
      setForm({
        brand_name: asText(b.brand_name),
        logo_url: asText(b.logo_url),
        primary_color: asText(b.primary_color),
        support_email: asText(b.support_email),
        custom_domain: asText(b.custom_domain),
      })
      setUpdatedAt(b.updated_at || null)
      toast.success('Branding saved')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save branding')
    } finally {
      setSaving(false)
    }
  }

  const previewColor = isHex(form.primary_color) ? form.primary_color.trim() : 'var(--rs-accent)'
  const brandInitial = (form.brand_name.trim()[0] || 'W').toUpperCase()

  return (
    <div className="rs-card rs-fade">
      <div className="flex items-center justify-between gap-2" style={{ marginBottom: 4 }}>
        <span className="rs-sec-h"><Palette size={16} style={{ color: 'var(--rs-accent)' }} /> Workspace branding</span>
        {updatedAt && <span className="rs-dim" style={{ fontSize: 12 }}>Updated {fmtDate(updatedAt)}</span>}
      </div>
      <p className="rs-sub" style={{ marginBottom: 18 }}>How this workspace presents itself to your customers.</p>

      {/* live preview */}
      <div className="rs-soft rs-fade" style={{ padding: 16, marginBottom: 18 }}>
        <p className="rs-elab" style={{ marginBottom: 12 }}>Live preview</p>
        <div className="flex items-center gap-3">
          {form.logo_url.trim()
            ? <img
                src={form.logo_url.trim()}
                alt=""
                className="rs-logo"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            : <span className="rs-icobox" style={{ width: 44, height: 44, borderRadius: 12, background: previewColor, color: '#fff', fontWeight: 800, fontSize: 18 }}>{brandInitial}</span>}
          <div className="min-w-0">
            <p className="rs-narr" style={{ fontSize: 17 }}>{form.brand_name.trim() || 'Your brand name'}</p>
            <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 5 }}>
              <span className="rs-chip"><span className="rs-swatch" style={{ width: 13, height: 13, borderRadius: 4, background: previewColor, boxShadow: 'none' }} /> {isHex(form.primary_color) ? form.primary_color.trim() : 'Default color'}</span>
              {form.custom_domain.trim() && <span className="rs-chip"><Globe size={11} /> {form.custom_domain.trim()}</span>}
              {form.support_email.trim() && <span className="rs-chip"><Mail size={11} /> {form.support_email.trim()}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* form */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="rs-lbl"><Tag size={12} /> Brand name</label>
          <input className="rs-input" value={form.brand_name} onChange={set('brand_name')} placeholder="Acme Diagnostics" disabled={loading} />
        </div>
        <div>
          <label className="rs-lbl"><ImageIcon size={12} /> Logo URL</label>
          <input className="rs-input" value={form.logo_url} onChange={set('logo_url')} placeholder="https://…/logo.png" disabled={loading} />
        </div>
        <div>
          <label className="rs-lbl"><Palette size={12} /> Primary color</label>
          <div className="flex items-center gap-2.5">
            <input
              type="color"
              value={isHex(form.primary_color) ? form.primary_color.trim() : '#4E9AC4'}
              onChange={set('primary_color')}
              disabled={loading}
              style={{ width: 44, height: 44, padding: 0, border: '1px solid var(--rs-line)', borderRadius: 12, background: 'var(--rs-panel2)', cursor: 'pointer', flex: 'none' }}
              aria-label="Pick primary color"
            />
            <input className="rs-input" value={form.primary_color} onChange={set('primary_color')} placeholder="#4E9AC4" disabled={loading} />
          </div>
        </div>
        <div>
          <label className="rs-lbl"><Mail size={12} /> Support email</label>
          <input className="rs-input" value={form.support_email} onChange={set('support_email')} placeholder="support@acme.com" disabled={loading} />
        </div>
        <div className="md:col-span-2">
          <label className="rs-lbl"><Globe size={12} /> Custom domain</label>
          <input className="rs-input" value={form.custom_domain} onChange={set('custom_domain')} placeholder="portal.acme.com" disabled={loading} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginTop: 18 }}>
        <span className="rs-dim" style={{ fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 5 }}>
          <ShieldCheck size={12} /> Branding applies to this workspace only.
        </span>
        <button onClick={save} disabled={saving || loading} className="rs-btn primary">
          {saving ? <RefreshCw size={14} className="rs-spin" /> : <Save size={14} />} {saving ? 'Saving…' : 'Save branding'}
        </button>
      </div>
    </div>
  )
}

// ---- OPERATOR PLANE (admins only; self-hides on 403) ----------------------
function OperatorPlane() {
  const [state, setState] = useState('loading') // loading | denied | ready | error
  const [resellers, setResellers] = useState([])

  const load = () => {
    setState('loading')
    axios.get(`${API}/`)
      .then(r => { setResellers(Array.isArray(r.data?.resellers) ? r.data.resellers : []); setState('ready') })
      .catch(err => {
        if (err.response?.status === 403 || err.response?.status === 401) setState('denied')
        else setState('error')
      })
  }
  useEffect(load, [])

  // Non-operators simply never see this plane.
  if (state === 'denied') return null

  return (
    <div className="rs-fade">
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <h3 className="rs-sec-h"><Store size={14} /> Resellers</h3>
        <div className="flex items-center gap-2">
          <span className="rs-pill" style={pill(TONE.pur)}><ShieldCheck size={12} /> Operator</span>
          <button onClick={load} disabled={state === 'loading'} className="rs-btn sm">
            <RefreshCw size={13} className={state === 'loading' ? 'rs-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {state === 'loading' && (
        <div className="rs-card rs-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>Loading resellers…</div>
      )}
      {state === 'error' && (
        <div className="rs-card" style={{ textAlign: 'center', padding: '32px 22px' }}>
          <AlertTriangle size={26} style={{ color: 'var(--rs-warn)', margin: '0 auto 8px' }} />
          <p className="rs-muted" style={{ fontSize: 13.5, margin: 0 }}>Couldn’t load resellers. Try refreshing.</p>
        </div>
      )}

      {state === 'ready' && <NewReseller onCreated={load} />}

      {state === 'ready' && resellers.length === 0 && (
        <div className="rs-card" style={{ textAlign: 'center', padding: '40px 22px' }}>
          <Store size={28} style={{ color: 'var(--rs-dim)', margin: '0 auto 8px' }} />
          <p className="rs-muted" style={{ fontSize: 13.5, margin: 0 }}>No resellers yet. Add one above to start assigning workspaces and tracking commissions.</p>
        </div>
      )}

      {state === 'ready' && resellers.length > 0 && (
        <div className="space-y-3">
          {resellers.map(r => <ResellerRow key={r.id} reseller={r} />)}
        </div>
      )}
    </div>
  )
}

function NewReseller({ onCreated }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [pct, setPct] = useState('20')
  const [busy, setBusy] = useState(false)

  const create = async () => {
    if (!name.trim()) { toast.error('Reseller name is required'); return }
    setBusy(true)
    try {
      await axios.post(`${API}/`, {
        name: name.trim(),
        owner_email: email.trim() || null,
        commission_pct: Number(pct) || 0,
      })
      toast.success('Reseller saved')
      setName(''); setEmail(''); setPct('20'); setOpen(false)
      onCreated()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save reseller')
    } finally { setBusy(false) }
  }

  if (!open) {
    return (
      <div style={{ marginBottom: 12 }}>
        <button onClick={() => setOpen(true)} className="rs-btn sm"><Plus size={13} /> Add reseller</button>
      </div>
    )
  }

  return (
    <div className="rs-card tight rs-fade" style={{ marginBottom: 12 }}>
      <p className="rs-elab" style={{ marginBottom: 12 }}>New reseller</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-1">
          <label className="rs-lbl"><Tag size={12} /> Name</label>
          <input className="rs-input" value={name} onChange={e => setName(e.target.value)} placeholder="Partner agency" />
        </div>
        <div className="sm:col-span-1">
          <label className="rs-lbl"><Mail size={12} /> Owner email</label>
          <input className="rs-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="owner@agency.com" />
        </div>
        <div className="sm:col-span-1">
          <label className="rs-lbl"><Percent size={12} /> Commission %</label>
          <input className="rs-input" type="number" min="0" max="100" value={pct} onChange={e => setPct(e.target.value)} placeholder="20" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2" style={{ marginTop: 14 }}>
        <button onClick={() => setOpen(false)} className="rs-btn ghost sm">Cancel</button>
        <button onClick={create} disabled={busy} className="rs-btn primary sm">
          {busy ? <RefreshCw size={13} className="rs-spin" /> : <Plus size={13} />} {busy ? 'Saving…' : 'Save reseller'}
        </button>
      </div>
    </div>
  )
}

function ResellerRow({ reseller }) {
  const [open, setOpen] = useState(false)
  const [tenants, setTenants] = useState(null)   // null = not loaded
  const [commissions, setCommissions] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.allSettled([
      axios.get(`${API}/${reseller.id}/tenants`),
      axios.get(`${API}/${reseller.id}/commissions`, { params: { limit: 100 } }),
    ]).then(([t, c]) => {
      setTenants(t.status === 'fulfilled' && Array.isArray(t.value.data?.tenants) ? t.value.data.tenants : [])
      setCommissions(c.status === 'fulfilled' ? (c.value.data || {}) : {})
    }).finally(() => setLoading(false))
  }

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && tenants === null) load()
  }

  const tenantCount = Number.isFinite(Number(reseller.tenant_count)) ? Number(reseller.tenant_count) : (tenants?.length || 0)
  const totalCents = commissions && Number.isFinite(Number(commissions.total_cents)) ? Number(commissions.total_cents) : null
  const entries = Array.isArray(commissions?.entries) ? commissions.entries : []

  return (
    <div className="rs-card tight rs-fade">
      <button onClick={toggle} className="rs-row-toggle flex items-center justify-between gap-3" style={{ background: 'none', border: 'none', padding: 0 }}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="rs-icobox" style={pill(TONE.acc)}><Store size={16} /></span>
          <div className="min-w-0 text-left">
            <p className="rs-narr" style={{ fontSize: 15 }}>{asText(reseller.name) || 'Unnamed reseller'}</p>
            <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 5 }}>
              {reseller.owner_email && <span className="rs-chip"><Mail size={11} /> {asText(reseller.owner_email)}</span>}
              <span className="rs-chip"><Percent size={11} /> {asText(reseller.commission_pct) || '0'}%</span>
              <span className="rs-chip"><Users size={11} /> {tenantCount} workspace{tenantCount === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>
        <ChevronRight size={18} style={{ color: 'var(--rs-mut)', transition: 'transform .2s', transform: open ? 'rotate(90deg)' : 'none', flex: 'none' }} />
      </button>

      {open && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--rs-line)', paddingTop: 14 }}>
          {loading && (tenants === null || commissions === null) && (
            <p className="rs-muted" style={{ fontSize: 13, margin: 0, textAlign: 'center', padding: '12px 0' }}>Loading detail…</p>
          )}

          {tenants !== null && (
            <>
              {/* commissions summary */}
              <div className="grid sm:grid-cols-2 gap-3" style={{ marginBottom: 14 }}>
                <div className="rs-soft" style={{ padding: 14 }}>
                  <p className="rs-elab" style={{ marginBottom: 6 }}><Wallet size={12} /> Commission total</p>
                  <p className="rs-narr rs-tabnum" style={{ fontSize: 22 }}>{totalCents == null ? '—' : usd(totalCents)}</p>
                  <p className="rs-dim" style={{ fontSize: 12, marginTop: 2 }}>{Number(commissions?.count) || entries.length} ledger entr{(Number(commissions?.count) || entries.length) === 1 ? 'y' : 'ies'}</p>
                </div>
                <div className="rs-soft" style={{ padding: 14 }}>
                  <p className="rs-elab" style={{ marginBottom: 6 }}><Users size={12} /> Managed workspaces</p>
                  <p className="rs-narr rs-tabnum" style={{ fontSize: 22 }}>{tenants.length}</p>
                  <p className="rs-dim" style={{ fontSize: 12, marginTop: 2 }}>at {asText(reseller.commission_pct) || '0'}% commission</p>
                </div>
              </div>

              {/* tenants list */}
              <p className="rs-elab" style={{ marginBottom: 8 }}><Link2 size={12} /> Workspaces</p>
              {tenants.length === 0 && (
                <p className="rs-dim" style={{ fontSize: 13, margin: '0 0 14px' }}>No workspaces linked to this reseller yet.</p>
              )}
              {tenants.length > 0 && (
                <div className="space-y-2" style={{ marginBottom: 14 }}>
                  {tenants.map(t => {
                    const stt = statusTone(t.status)
                    return (
                      <div key={t.namespace_id} className="flex items-center justify-between gap-3 rs-soft" style={{ padding: '10px 13px' }}>
                        <div className="min-w-0">
                          <p className="rs-body" style={{ fontSize: 13.5 }}>{asText(t.name) || asText(t.slug) || asText(t.namespace_id)}</p>
                          <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 4 }}>
                            {t.slug && <span className="rs-dim" style={{ fontSize: 11.5 }}>{asText(t.slug)}</span>}
                            {t.plan && <span className="rs-chip">{asText(t.plan)}</span>}
                            <span className="rs-dim" style={{ fontSize: 11.5 }}>linked {fmtDate(t.created_at)}</span>
                          </div>
                        </div>
                        {t.status && <span className="rs-pill" style={pill(stt)}>{asText(t.status)}</span>}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* commission ledger */}
              {entries.length > 0 && (
                <>
                  <p className="rs-elab" style={{ marginBottom: 8 }}><Wallet size={12} /> Recent commissions</p>
                  <div className="space-y-2">
                    {entries.slice(0, 8).map(e => (
                      <div key={e.id} className="flex items-center justify-between gap-3 rs-soft" style={{ padding: '9px 13px' }}>
                        <div className="min-w-0">
                          <p className="rs-body rs-tabnum" style={{ fontSize: 13.5 }}>{usd(e.amount_cents)}</p>
                          <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 3 }}>
                            {e.namespace_id && <span className="rs-dim" style={{ fontSize: 11.5 }}>{asText(e.namespace_id)}</span>}
                            {e.note && <span className="rs-muted" style={{ fontSize: 11.5 }}>{asText(e.note)}</span>}
                          </div>
                        </div>
                        <span className="rs-dim" style={{ fontSize: 11.5, flex: 'none' }}>{fmtDate(e.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
